import {
	collectionGroup,
	doc,
	type Firestore,
	type FirestoreDataConverter,
	getDoc,
	Query,
	type QueryDocumentSnapshot,
	type SnapshotOptions,
} from "firebase/firestore";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function ensureString(value: unknown): string | undefined {
	if (typeof value === "string" && value.trim().length > 0) return value;
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	return undefined;
}

function parseDateString(value: unknown): string | undefined {
	if (typeof value === "string" && value) return value;
	if (typeof value === "number" && Number.isFinite(value)) {
		return new Date(
			value >= 1_000_000_000_000 ? value : value * 1000,
		).toISOString();
	}
	if (isRecord(value) && typeof value.seconds === "number") {
		return new Date(value.seconds * 1000).toISOString();
	}
	return undefined;
}

import type {
	CallPathInfo,
	RecordingManifest,
	TranscriptEntry,
} from "@repo/common";

export { type TranscriptEntry, type RecordingManifest, type CallPathInfo };

export interface AgentTranscript {
	transcript: {
		items: unknown[];
	};
}

export interface RecordingTranscripts {
	gcsUri?: string;
	transcripts?: {
		transcriptText: string;
		recognizerId?: string;
	}[];
}

export interface RecordingManifestResource {
	recordingUrl?: string;
}

export interface ProcessedTranscripts {
	formattedTranscript?: string;
	summary?: string;
	title?: string;
}

const tcdrConverter: FirestoreDataConverter<TcdrRecord> = {
	toFirestore(value) {
		return value.raw ?? {};
	},
	fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions) {
		const data = snapshot.data(options);
		const raw = isRecord(data) ? data : {};
		const callPath = snapshot.ref.path;
		const resourcesPath = `${callPath}/resources`;
		return {
			id: snapshot.id,
			raw,
			callPath,
			resourcesPath,
		} as TcdrRecord;
	},
};

export function tcdrsCollectionGroup(db: Firestore): Query<TcdrRecord> {
	// Matches docs stored under cdrs/{phone}/calls/{callSid}/cdrs/{index}
	return collectionGroup(db, "cdrs").withConverter(tcdrConverter);
}

/** Load a call by its exact partition, independently of the recent-calls page. */
export async function fetchTcdrByPath(
	db: Firestore,
	phoneNumber: string,
	callSid: string,
): Promise<TCDR | null> {
	if (
		!phoneNumber ||
		!callSid ||
		phoneNumber.includes("/") ||
		callSid.includes("/")
	)
		throw new Error("Invalid call diagnostics link");
	const path = `twilioCdrs/${phoneNumber}/cdrs/${callSid}`;
	const call = await getDoc(doc(db, path).withConverter(tcdrConverter));
	if (!call.exists()) return null;
	const [manifest, recording, processed] = await Promise.all(
		["recordingManifest", "recordingTranscripts", "processedTranscripts"].map(
			(name) => getDoc(doc(db, `${path}/resources/${name}`)),
		),
	);
	return toTcdrUi(
		call.data(),
		manifest.exists() ? parseRecordingManifest(manifest.data()) : undefined,
		recording.exists()
			? parseRecordingTranscripts(recording.data())
			: undefined,
		processed.exists()
			? parseProcessedTranscripts(processed.data())
			: undefined,
	);
}

export function compareTcdrRecords(a: TcdrRecord, b: TcdrRecord): number {
	const aCreated = parseDateString(a.raw["dateCreated"]) ?? "";
	const bCreated = parseDateString(b.raw["dateCreated"]) ?? "";
	const aTime = aCreated ? Date.parse(aCreated) : 0;
	const bTime = bCreated ? Date.parse(bCreated) : 0;
	return bTime - aTime;
}

function ensureRecordOfUnknown(
	value: unknown,
): Record<string, unknown> | undefined {
	return isRecord(value) ? value : undefined;
}

function ensureLegacyTranscripts(
	value: unknown,
): TranscriptEntry[] | undefined {
	if (!isRecord(value)) {
		return undefined;
	}

	const entries = Object.entries(value)
		.map(([key, val]) => {
			const transcriptText = ensureString(val);
			if (!transcriptText) {
				return undefined;
			}
			return { recognizerId: key, transcriptText } as TranscriptEntry;
		})
		.filter((entry): entry is TranscriptEntry => Boolean(entry));

	return entries.length > 0 ? entries : undefined;
}

export function parseRecordingTranscripts(
	value: unknown,
): RecordingTranscripts | undefined {
	if (!isRecord(value)) return undefined;
	const gcsUri = ensureString(value["gcsUri"]);
	const transcriptsRaw = value["transcripts"];

	let transcripts:
		| { transcriptText: string; recognizerId: string }[]
		| undefined;

	if (Array.isArray(transcriptsRaw)) {
		transcripts = transcriptsRaw
			.map((t) => {
				if (!isRecord(t)) return null;
				const text = ensureString(t["transcriptText"]);
				if (!text) return null;
				return {
					transcriptText: text,
					recognizerId: ensureString(t["recognizerId"]) ?? "unknown",
				};
			})
			.filter(
				(t): t is { transcriptText: string; recognizerId: string } =>
					t !== null,
			);
	}

	if (!gcsUri && (!transcripts || transcripts.length === 0)) return undefined;

	return { gcsUri, transcripts };
}

export function parseProcessedTranscripts(
	value: unknown,
): ProcessedTranscripts | undefined {
	if (!isRecord(value)) return undefined;

	const formattedTranscript = ensureString(value["formattedTranscript"]);
	const summary = ensureString(value["summary"]);
	const title = ensureString(value["title"]);

	return { formattedTranscript, summary, title };
}

export function parseRecordingManifest(
	value: unknown,
): RecordingManifestResource | undefined {
	if (!isRecord(value)) return undefined;

	const recordings = value["recordings"];
	if (!Array.isArray(recordings) || recordings.length === 0) {
		return undefined;
	}

	const firstRecording = recordings[0];
	if (!isRecord(firstRecording)) {
		return undefined;
	}

	const mediaUrl = ensureString(firstRecording["media_url"]);
	if (!mediaUrl) {
		return undefined;
	}

	return {
		recordingUrl: mediaUrl.endsWith(".mp3") ? mediaUrl : `${mediaUrl}.mp3`,
	};
}

function getString(
	raw: Record<string, unknown>,
	...keys: string[]
): string | undefined {
	for (const key of keys) {
		const result = ensureString(raw[key]);
		if (result !== undefined) {
			return result;
		}
	}
	return undefined;
}

export function toTcdrUi(
	record: TcdrRecord,
	recordingManifest?: RecordingManifestResource,
	recordingTranscripts?: RecordingTranscripts,
	processedTranscripts?: ProcessedTranscripts,
) {
	const raw = record.raw as Record<string, unknown>;
	const sid = ensureString(raw.sid) ?? record.id;

	const legacyTranscripts = ensureLegacyTranscripts(raw.transcripts);

	// Convert recordingTranscripts to TranscriptEntry[]
	const recordingTranscriptEntries = recordingTranscripts?.transcripts?.map(
		(t) =>
			({
				recognizerId: t.recognizerId ?? "twilio",
				transcriptText: t.transcriptText,
			}) as TranscriptEntry,
	);

	const combinedTranscripts = [
		...(legacyTranscripts ?? []),
		...(recordingTranscriptEntries ?? []),
	];

	// If processed transcripts exist, add them as "formatted" or "summary" if needed,
	// but mostly we might want to store summary/title on the TCDR object itself or in a specific way.
	// For now, let's inject formattedTranscript as a transcript entry if present.
	if (processedTranscripts?.formattedTranscript) {
		combinedTranscripts.push({
			recognizerId: "formatted",
			transcriptText: processedTranscripts.formattedTranscript,
		});
	}

	return {
		...raw,
		sid,
		callPath: record.callPath,
		accountSid: getString(raw, "accountSid"),
		answeredBy: getString(raw, "answeredBy"),
		apiVersion: getString(raw, "apiVersion"),
		callerName: getString(raw, "callerName"),
		dateCreated: parseDateString(raw.dateCreated),
		dateUpdated: parseDateString(raw.dateUpdated),
		direction: getString(raw, "direction"),
		duration: getString(raw, "duration"),
		endTime: parseDateString(raw.endTime),
		forwardedFrom: getString(raw, "forwardedFrom", "forwarded_from"),
		from: getString(raw, "from"),
		fromFormatted: getString(raw, "fromFormatted", "from_formatted"),
		groupSid: getString(raw, "groupSid"),
		parentCallSid: getString(raw, "parentCallSid"),
		phoneNumberSid: getString(raw, "phoneNumberSid"),
		price: getString(raw, "price"),
		priceUnit: getString(raw, "priceUnit"),
		queueTime: getString(raw, "queueTime"),
		recordingUrl: recordingManifest?.recordingUrl,
		gcsUri: recordingTranscripts?.gcsUri,
		startTime: parseDateString(raw.startTime),
		status: getString(raw, "status"),
		subresourceUris: ensureRecordOfUnknown(raw.subresourceUris),
		to: getString(raw, "to"),
		toFormatted: getString(raw, "toFormatted", "to_formatted"),
		transcript: getString(raw, "transcript"),
		transcripts: combinedTranscripts,
		trunkSid: getString(raw, "trunkSid"),
		uri: getString(raw, "uri"),
		// Add new fields from processed transcripts
		summary: processedTranscripts?.summary,
		title: processedTranscripts?.title,
	};
}

export function getCallPathInfoFromManifestPath(
	manifestPath?: string,
): CallPathInfo | undefined {
	if (!manifestPath) {
		return undefined;
	}

	const segments = manifestPath.split("/");
	const cdrIndex = segments.indexOf("cdrs");

	if (cdrIndex === -1) {
		return undefined;
	}

	const phoneNumber = segments[cdrIndex + 1];
	const callSid = segments[cdrIndex + 3];

	if (!phoneNumber || !callSid) {
		return undefined;
	}

	const callPathSegments = segments.slice(0, cdrIndex + 4);
	const callPath = callPathSegments.join("/");

	return {
		callPath,
		phoneNumber,
		callSid,
	} satisfies CallPathInfo;
}

export type TCDR = ReturnType<typeof toTcdrUi>;
export type TcdrRecord = import("@repo/common").TcdrRecord & {
	resourcesPath?: string;
};
